"use client"

import { useClientTranslations } from "@/hooks/useClientTranslations"
import { api } from "@/lib/api"
import { useSandboxSessionStore } from "@/stores/sandboxSessionStore"
import { xErrorCodeOf } from "@/utils/trpcError.utils"
import { SANDBOX_SESSION_CAP_EXCEEDED_CODE } from "@/constants/sandboxErrorCodes.constants"

const sonnerModule = import("sonner")
const showSandboxToastError = (message: string) =>
    void sonnerModule.then(mod => mod.toast.error(message))

export function useSandboxSession() {
    const t = useClientTranslations("desktop.dock")
    const sandboxSessionId = useSandboxSessionStore(s => s.sandboxSessionId)

    const startSession = api.sandbox.start.useMutation({
        onSuccess: result => {
            useSandboxSessionStore
                .getState()
                .startSandboxSession(result.sessionId)
            useSandboxSessionStore
                .getState()
                .setExpiresAt(Date.parse(result.expiresAt))
        },
        onError: error =>
            showSandboxToastError(
                xErrorCodeOf(error) === SANDBOX_SESSION_CAP_EXCEEDED_CODE
                    ? t("sandboxSessionCapExceeded")
                    : t("sandboxStartFailed")
            ),
    })

    const leaveSession = api.sandbox.leave.useMutation({
        onSuccess: () => useSandboxSessionStore.getState().endSandboxSession(),
        onError: () => showSandboxToastError(t("sandboxLeaveFailed")),
    })

    const leave = () => {
        if (!sandboxSessionId) return
        leaveSession.mutate({ sessionId: sandboxSessionId })
    }

    return {
        sandboxSessionId,
        isStarting: startSession.isPending,
        isLeaving: leaveSession.isPending,
        start: () => startSession.mutate(),
        leave,
    }
}
